import { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import axios from "axios";
import { Ban, CheckCircle2, Download, Mail, Printer } from "lucide-react";
import { API } from "@/config";
import { getLang, t } from "@/lib/i18n";
import { setRobots } from "@/lib/seo";
import { path } from "@/lib/routes";
import { fmt, longDate, tl } from "@/lib/booking";
import { ParkNote } from "@/components/booking/SummaryStep";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

function EmailState({ tk }) {
  if (!tk.email) return null;
  const key = tk.email_state === "sent" ? "emailSent" : tk.email_state === "failed" ? "emailFailed" : "emailPending";
  return (
    <p className="flex items-start gap-2 text-sm text-ink-muted" data-testid="ticket-email-state" data-state={tk.email_state || "pending"}>
      <Mail className="h-4 w-4 mt-0.5 shrink-0" aria-hidden="true" /> {fmt(t(`bilet.ticket.${key}`), { email: tk.email })}
    </p>
  );
}

function CancelButton({ onConfirm, busy }) {
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <button type="button" disabled={busy} className="inline-flex items-center justify-center gap-2 min-h-[48px] px-6 rounded-full border border-[#B42318]/40 text-[#B42318] font-semibold hover:bg-[#FEF3F2]" data-testid="ticket-cancel-button">
          <Ban className="h-4 w-4" aria-hidden="true" /> {t("bilet.ticket.cancel")}
        </button>
      </AlertDialogTrigger>
      <AlertDialogContent data-testid="ticket-cancel-dialog">
        <AlertDialogTitle>{t("bilet.ticket.cancel")}</AlertDialogTitle>
        <AlertDialogDescription>{t("bilet.ticket.cancelConfirm")}</AlertDialogDescription>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="ticket-cancel-no">{t("bilet.ticket.cancelNo")}</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm} className="bg-[#B42318] hover:bg-[#912018]" data-testid="ticket-cancel-yes">{t("bilet.ticket.cancelYes")}</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export default function BiletDetay() {
  const { token } = useParams();
  const { state } = useLocation();
  const [tk, setTk] = useState(state?.ticket || null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const lang = getLang();

  const load = useCallback(() => axios.get(`${API}/tickets/${token}`).then((r) => setTk(r.data)).catch((e) => setError(e.response?.status === 404 ? "notFound" : "error")), [token]);

  useEffect(() => {
    document.title = t("seo.ticket.title");
    setRobots(true);
    load();
    const timers = [4000, 10000].map((ms) => setTimeout(load, ms));
    return () => timers.forEach(clearTimeout);
  }, [load]);

  const cancel = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/tickets/${token}/cancel`);
      setTk(data);
    } catch (e) {
      setError("error");
    } finally {
      setBusy(false);
    }
  };

  if (!tk) {
    return (
      <div className="safe-x max-w-3xl mx-auto py-24" data-testid="ticket-page">
        <p role={error ? "alert" : undefined} className="text-ink-muted" data-testid="ticket-not-found">{error ? t(`bilet.ticket.${error}`) : t("bilet.loading")}</p>
      </div>
    );
  }
  const inactive = tk.status !== "active";
  const rows = [
    [t("bilet.ticket.date"), longDate(tk.date)],
    [t("bilet.contact.name"), tk.name],
    ...["adult", "child", "free"].filter((k) => tk.counts[k]).map((k) => [t(`bilet.counts.${k}`), String(tk.counts[k])]),
    ...(tk.plate ? [[t("bilet.ticket.plate"), tk.plate]] : []),
  ];

  return (
    <div className="safe-x max-w-5xl mx-auto py-12 sm:py-16" data-testid="ticket-page" data-status={tk.status}>
      <div className="flex items-center gap-3 text-forest">
        <CheckCircle2 className="h-7 w-7 text-[#05603A]" aria-hidden="true" />
        <h1 className="font-serif text-4xl sm:text-5xl">{t("bilet.ticket.title")}</h1>
      </div>
      {inactive && (
        <p role="status" className="mt-6 rounded-xl bg-[#FEF3F2] px-4 py-3 font-semibold text-[#B42318]" data-testid="ticket-status-banner">
          {tk.status === "cancelled" ? t("bilet.ticket.cancelled") : t("bilet.ticket.used")}
        </p>
      )}
      <div className="mt-8 grid gap-8 md:grid-cols-[minmax(0,340px)_1fr]">
        <div className={`rounded-3xl bg-white border border-line p-6 text-center ${inactive ? "opacity-40" : ""}`}>
          <img src={`${API}/tickets/${tk.token}/qr.png`} alt={fmt(t("bilet.ticket.qrAlt"), { code: tk.code })} width="290" height="290" className="mx-auto w-full max-w-[290px] h-auto [image-rendering:pixelated]" data-testid="ticket-qr-image" />
          <p className="mt-4 text-sm text-ink-muted">{t("bilet.ticket.code")}</p>
          <p className="font-mono text-3xl font-bold tracking-wider text-forest" data-testid="ticket-code">{tk.code}</p>
          <p className="mt-3 text-sm text-ink">{t("bilet.ticket.show")}</p>
        </div>
        <div className="space-y-5">
          <dl className="rounded-2xl border border-line bg-white divide-y divide-line">
            {rows.map(([k, val]) => (
              <div key={k} className="flex flex-wrap justify-between gap-2 px-5 py-3">
                <dt className="text-ink-muted">{k}</dt>
                <dd className="font-semibold text-ink">{val}</dd>
              </div>
            ))}
            <div className="flex flex-wrap justify-between items-baseline gap-2 px-5 py-4 bg-cream-2">
              <dt className="font-semibold text-forest">{t("bilet.ticket.due")}</dt>
              <dd className="font-serif text-3xl text-forest" data-testid="ticket-amount-due">{tl(tk.amount_due)}</dd>
            </div>
          </dl>
          <p className="font-semibold text-forest" data-testid="ticket-pre-note">{t("bilet.preNote")}</p>
          <ParkNote testId="ticket-park-note" />
          <EmailState tk={tk} />
          <div className="no-print flex flex-wrap gap-3 pt-2">
            <a href={`${API}/tickets/${tk.token}/ticket.png?lang=${lang}`} download={`woodia-${tk.code}.png`} className="inline-flex items-center gap-2 min-h-[48px] px-6 rounded-full bg-forest text-cream font-semibold hover:bg-forest-dark" data-testid="ticket-download-button">
              <Download className="h-4 w-4" aria-hidden="true" /> {t("bilet.ticket.download")}
            </a>
            <button type="button" onClick={() => window.print()} className="inline-flex items-center gap-2 min-h-[48px] px-6 rounded-full border border-forest/30 text-forest font-semibold hover:bg-forest/[0.05]" data-testid="ticket-print-button">
              <Printer className="h-4 w-4" aria-hidden="true" /> {t("bilet.ticket.print")}
            </button>
            {tk.can_cancel && <CancelButton onConfirm={cancel} busy={busy} />}
          </div>
          {tk.can_cancel && <p className="no-print text-sm text-ink-muted">{t("bilet.ticket.cancelHint")}</p>}
          {error && tk && <p role="alert" className="text-sm text-[#B42318]" data-testid="ticket-error">{t(`bilet.ticket.${error}`)}</p>}
          <Link to={path("bilet")} className="no-print inline-block text-sm font-semibold text-forest underline underline-offset-4" data-testid="ticket-new-booking-link">{t("bilet.ticket.newBooking")}</Link>
        </div>
      </div>
    </div>
  );
}
